/**
 * =============================================================================
 * MAINTENANCE CHECK - Verificação de Modo de Manutenção
 * =============================================================================
 *
 * Verifica junto da API PHP se o site está em modo de manutenção.
 * Se estiver, apresenta uma página de manutenção com login para clientes autorizados.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Settings } from "lucide-react";
import { Spinner } from "./Spinner";
import { Button } from "./Button";
import { FormField } from "./FormField";
import { FormInput } from "./FormInput";
import { FormError } from "./FormError";
import { apiFetch, API_ENDPOINTS } from "@/lib/api";
import { clientLoginSchema, type ClientLoginData } from "@/lib/validation";
import type { MaintenanceStatus, Client } from "@/types/api";

export function MaintenanceCheck({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<MaintenanceStatus | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ClientLoginData>({
    resolver: zodResolver(clientLoginSchema),
  });

  useEffect(() => {
    const checkStatus = async () => {
      try {
        console.log('[MaintenanceCheck] A verificar estado...');

        const maintenanceRes = await apiFetch<MaintenanceStatus>(`${API_ENDPOINTS.maintenance.status}?t=${Date.now()}`);
        console.log('[MaintenanceCheck] Resposta status:', maintenanceRes);
        setStatus(maintenanceRes.data || { active: false, message: "" });
      } catch (err) {
        console.error('[MaintenanceCheck] Erro ao obter status:', err);
        setStatus({ active: false, message: "" });
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      }

      try {
        const clientRes = await apiFetch<Client>(`${API_ENDPOINTS.auth.clientMe}?t=${Date.now()}`);
        console.log('[MaintenanceCheck] Resposta cliente:', clientRes);
        setClient(clientRes.data || null);
      } catch (err) {
        console.log('[MaintenanceCheck] Cliente nao autenticado:', err);
        setClient(null);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []);

  const handleLogin = async (data: ClientLoginData) => {
    setAuthLoading(true);
    setError(null);

    try {
      const response = await apiFetch<Client>(API_ENDPOINTS.auth.clientLogin, {
        method: "POST",
        body: data,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || "Erro ao iniciar sessão");
      }

      setClient(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao iniciar sessão");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiFetch(API_ENDPOINTS.auth.clientLogout, { method: "POST" });
      setClient(null);
    } catch {
      setError("Erro ao terminar sessão");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-seaMist">
        <div className="text-center">
          <Spinner size={40} className="text-oceanBlue mx-auto mb-4" label="A carregar..." />
          <p className="text-textMuted">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!status?.active || client) {
    return (
      <>
        {client && status?.active && (
          <div className="fixed right-4 top-20 z-50 flex items-center gap-3 rounded-lg bg-oceanBlue px-4 py-2 text-sm text-white shadow-lg">
            <span>Modo cliente: {client.name || client.email}</span>
            <button
              onClick={handleLogout}
              className="rounded bg-white/20 px-2 py-0.5 transition hover:bg-white/30"
            >
              Sair
            </button>
          </div>
        )}
        {children}
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-seaMist px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-pureWhite p-8 shadow-lg">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-oceanBlue/10">
            <Settings size={32} className="text-oceanBlue" aria-hidden="true" />
          </div>

          <h1 className="mb-2 text-center text-2xl font-semibold text-deepOcean">
            Em manutenção
          </h1>

          <p className="mb-6 text-center text-textBody/70">
            {status.message || "Estamos em manutenção. Voltamos brevemente."}
          </p>

          <div className="mb-6 border-t border-seaMist pt-6">
            <h2 className="mb-4 text-center text-lg font-medium text-deepOcean">
              Acesso para clientes
            </h2>

            <form onSubmit={handleSubmit(handleLogin)} className="space-y-4">
              <FormField id="email" label="Email" error={errors.email?.message} required>
                <FormInput
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="o.teu@email.pt"
                  {...register("email")}
                />
              </FormField>

              <FormField id="password" label="Password" error={errors.password?.message} required>
                <FormInput
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...register("password")}
                />
              </FormField>

              {error && <FormError message={error} />}

              <Button type="submit" isLoading={authLoading} className="w-full">
                Entrar para ver o site
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-textMuted">
            Se não tens credenciais de acesso, entra em contacto connosco.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceCheck;
